import moment from "moment";
import _ from "lodash";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

export const generateDates = (startFrom, numDays) => {
  const dates = [];
  const currentDate = new Date(startFrom);

  for (let i = 0; i < numDays; i++) {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + i);

    const day = newDate.toLocaleDateString("en-US", { weekday: "short" }); // e.g., 'Mon'
    const date = newDate.getDate(); // e.g., 7
    const month = newDate.toLocaleDateString("en-US", { month: "long" }); // e.g., 'October'
    const fullDate = moment(newDate).format("MM/DD/YYYY");

    dates.push({ day, date, month, fullDate });
  }

  return dates;
};

export const monthChecker = (month) => {
  switch (month) {
    case "01":
      return "January";
    case "02":
      return "February";
    case "03":
      return "March";
    case "04":
      return "April";
    case "05":
      return "May";
    case "06":
      return "June";
    case "07":
      return "July";
    case "08":
      return "August";
    case "09":
      return "September";
    case "10":
      return "October";
    case "11":
      return "November";
    case "12":
      return "December";
    default:
      return "Invalid Month";
  }
};

export const changeIntoString = (date) => {
  const month = monthChecker(date.slice(0, 2));
  const day = date.slice(3, 5);
  const year = date.slice(6);
  return `${day} ${month} ${year}`;
};

export const generateTimeSlots = (selectedDate, slotConfigs) => {
  try {
    const selectedMoment = moment(selectedDate, "MM/DD/YYYY", true);
    const today = moment();

    if (!selectedMoment.isValid()) {
      throw new Error("Invalid selected date: " + selectedDate);
    }

    const validSlots = slotConfigs.filter((config) => {
      const toDateMoment = moment(config.toDate, "MM/DD/YYYY", true);
      return config.status === 1 && toDateMoment.isSameOrAfter(today, "day");
    });

    let finalMorningSlots = [];
    let finalEveningSlots = [];

    for (const config of validSlots) {
      const {
        fromDate,
        toDate,
        fromTime,
        toTime,
        consultationDuration,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
        sunday,
      } = config;

      const startMoment = moment(fromDate, "MM/DD/YYYY", true);
      const endMoment = moment(toDate, "MM/DD/YYYY", true);

      if (!selectedMoment.isBetween(startMoment, endMoment, "days", "[]")) {
        continue;
      }

      const dayName = selectedMoment.format("ddd");
      const dayAvailability = {
        Mon: monday === "Y",
        Tue: tuesday === "Y",
        Wed: wednesday === "Y",
        Thu: thursday === "Y",
        Fri: friday === "Y",
        Sat: saturday === "Y",
        Sun: sunday === "Y",
      };

      if (!dayAvailability[dayName]) {
        continue;
      }

      const slotDuration = consultationDuration
        ? parseInt(consultationDuration, 10)
        : 15;
      // if (isNaN(slotDuration) || slotDuration <= 0) {
      //     console.warn("Invalid consultation duration:", consultationDuration);
      //     continue;
      // }

      const morningEnd = moment("12:00", "HH:mm");
      const now = moment();
      const isToday = selectedMoment.isSame(now, "day");

      let currentSlot = moment(fromTime, "HH:mm");
      const endTime = moment(toTime, "HH:mm");

      const morningSlots = [];
      const eveningSlots = [];

      while (currentSlot.isBefore(endTime)) {
        if (isToday && currentSlot.isBefore(now)) {
          currentSlot = currentSlot.add(slotDuration, "minutes");
          continue;
        }

        const formattedSlot = currentSlot.format("HH:mm");

        if (currentSlot.isBefore(morningEnd)) {
          morningSlots.push(formattedSlot);
        } else {
          eveningSlots.push(formattedSlot);
        }

        currentSlot = currentSlot.add(slotDuration, "minutes");
      }

      finalMorningSlots = Array.from(
        new Set([...finalMorningSlots, ...morningSlots])
      );
      finalEveningSlots = Array.from(
        new Set([...finalEveningSlots, ...eveningSlots])
      );
    }

    return { morningSlots: finalMorningSlots, eveningSlots: finalEveningSlots };
  } catch (error) {
    console.error("Error in generateTimeSlots:", error);
    return null;
  }
};

export const groupSlotsByDoctor = (slots, day, currentDateStr) => {
  const currentDate = dayjs(currentDateStr);

  const isSlotValidToday = (slot) => {
    const slotFrom = dayjs(slot.fromDate);
    const slotTo = dayjs(slot.toDate);
    // return (
    //     slot.status === 1 &&
    //     currentDate >= slotFrom &&
    //     currentDate <= slotTo &&
    //     slot[day.toLowerCase()] === "Y"
    // );

    return (
      slot.status === 1 &&
      currentDate.isBetween(slotFrom, slotTo, null, "[]") &&
      slot[day.toLowerCase()] === "Y"
    );
  };

  const filtered = slots.filter(isSlotValidToday);

  // Group by userId
  const grouped = _.groupBy(filtered, "userId");

  // Convert to array of doctors with their slots
  return Object.values(grouped).map((doctorSlots) => ({
    ...doctorSlots[0], // take profile info from first slot
    slots: doctorSlots, // all slots of the doctor for the day
  }));
};

export const generateDocTimeSlots = (
  fromTime,
  toTime,
  intervalInMinutes = 10
) => {
  const pad = (n) => (n < 10 ? "0" + n : n);
  const parse = (str) => {
    const [h, m] = str.split(":").map(Number);
    return h * 60 + m;
  };
  const format = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${pad(hour12)}:${pad(m)} ${ampm}`;
  };

  const start = parse(fromTime);
  const end = parse(toTime);
  const result = [];

  for (let t = start; t < end; t += intervalInMinutes) {
    result.push(format(t));
  }

  return result;
};

export const categorizeTimeSlots = (slots, selectedDate) => {
  const now = moment(); // current time
  const selectedMoment = moment(selectedDate, "MM/DD/YYYY");
  const isToday = selectedMoment.isSame(now, "day");

  const morning = [];
  const afternoon = [];
  const evening = [];

  slots.forEach((time) => {
    const slotMoment = moment(time, "hh:mm A");
    // Attach the selected date to the slot time for full datetime comparison
    slotMoment.set({
      year: selectedMoment.year(),
      month: selectedMoment.month(),
      date: selectedMoment.date(),
    });

    const isExpired = isToday && slotMoment.isBefore(now);
    const slotData = { time, isExpired };

    if (slotMoment.hour() < 12) {
      morning.push(slotData);
    } else if (slotMoment.hour() < 17) {
      afternoon.push(slotData);
    } else {
      evening.push(slotData);
    }
  });

  return { morning, afternoon, evening };
};

// export const filterCurrentMonthVitals = (dataArray) =>{
//   const now = new Date();
//   const currentMonth = now.getMonth(); // 0-indexed: Jan = 0
//   const currentYear = now.getFullYear();

//   return dataArray.filter(item => {
//     const itemDate = new Date(item.recordedDateTime);
//     return (
//       itemDate.getMonth() === currentMonth &&
//       itemDate.getFullYear() === currentYear
//     );
//   });
// }

export const filterCurrentMonthVitals = (dataArray, selectedMonth) => {
  const month = selectedMonth.month(); // dayjs: 0-indexed
  const year = selectedMonth.year();

  return dataArray.filter((item) => {
    const itemDate = new Date(item.recordedDateTime);
    return itemDate.getMonth() === month && itemDate.getFullYear() === year;
  });
};
